/**
 * @author      yontaek@zumper.com (Yon Taek Chung)
 * @copyright   Copyright (c) 2024, Zumper
 */

import { spotlightStar } from '@zcode-ui/icons'
import { cn } from '@zcode-ui/shadz-ui'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import React from 'react'
import { FavoriteContainer } from '../../../../favorite/FavoriteContainer'
import { CarouselWrapper } from '../STRListItem/CarouselWrapper'

export interface SpotlightListingImageSectionProps {
  entity: Listable | Pin<RawPinInventorySearch>
  forceIntersected?: boolean
  isLargeSpotlight?: boolean
  parentName?: string
  shouldPreloadImage?: boolean
}

export type ImageContainerProps = {
  readonly borderRadius: string
}

export const SpotlightListingImageSection = ({
  entity,
  forceIntersected,
  isLargeSpotlight,
  parentName,
  shouldPreloadImage,
}: SpotlightListingImageSectionProps) => {
  return (
    <div
      className={cn(
        'h-[350px] max-h-[350px] min-h-[350px] relative',
        isLargeSpotlight &&
          'h-[444px] max-h-[444px] min-h-[444px] overflow-hidden rounded-[24px_0_0_24px]',
        '[&>img]:w-full [&>img]:cursor-pointer [&>img]:object-cover'
      )}
    >
      <CarouselWrapper
        customImageHeight={isLargeSpotlight ? 444 : 350}
        customImageWidth={isLargeSpotlight ? 792 : undefined}
        entity={entity}
        forceIntersected={forceIntersected}
        groupId={entity.groupId}
        hasImage={entity.hasImage}
        images={entity.images}
        isSpotlightVariation={!isLargeSpotlight}
        shouldPreloadImage={shouldPreloadImage}
      />
      <div
        data-testid="must-see"
        className={cn(
          'absolute top-4 left-4 gap-0.5 flex items-center justify-center w-[104px] h-[22px] bg-gray-0 rounded-sm',
          isLargeSpotlight && 'top-6 left-6'
        )}
      >
        <div>
          <img alt="" src={spotlightStar} />
        </div>
        <p className="text-gray-800 text-sm font-body leading-5 font-normal">Must-see</p>
      </div>
      <div className={cn('absolute top-3 right-2', isLargeSpotlight && 'top-4 right-3')}>
        <FavoriteContainer
          borderStyle="none"
          entity={entity}
          fill="#AAA9A7"
          fillOpacity={0.6}
          parentName={parentName}
          shouldUseTransparentBg
          size={24}
          stroke="#ffffff"
          strokeFavorite="#ffffff"
          strokeHover="#ffffff"
        />
      </div>
    </div>
  )
}
